import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { uploadBanner } from "./BannerUpload";
import {
  getBannerDimensionError,
  getBannerFileSizeError,
  isBannerImageFile,
  loadImageDimensions,
} from "./bannerFileValidation";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
} from "firebase/storage";
import { app } from "../../../../Features/config/firebase";
import "./BannerSwapForm.css";

interface FileUploadAreaProps {
  onImageUrlChange: (newImageUrl: string | null) => void;
  onImageNameChange: (newImageName: string | null) => void;
}

export interface FileUploadAreaRef {
  upload: () => Promise<{ url: string; imageName: string }>;
  hasFile: () => boolean;
  clear: () => void;
}

const FileUploadArea = forwardRef<FileUploadAreaRef, FileUploadAreaProps>(
  ({ onImageUrlChange, onImageNameChange }, ref) => {
    const toast = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [validating, setValidating] = useState(false);

    const handleChooseClick = () => {
      fileInputRef.current?.click();
    };

    const clearSelection = () => {
      setSelectedFile(null);
      setPreviewUrl(null);
      onImageUrlChange(null);
      onImageNameChange(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    const showValidationError = (detail: string) => {
      toast.current?.show({
        severity: "error",
        summary: "Invalid banner",
        detail,
        life: 6000,
      });
      clearSelection();
    };

    const performUpload = async (
      file: File
    ): Promise<{ url: string; imageName: string }> => {
      setUploading(true);

      try {
        // Check if file already exists in storage
        const storage = getStorage(app, "gs://platforms-tech.appspot.com");
        const fileRef = storageRef(storage, "/channel-banners/" + file.name);

        try {
          await getDownloadURL(fileRef);
          // File exists
          toast.current?.show({
            severity: "error",
            summary: "Upload Failed",
            detail: "Banner with the same name already exists",
            life: 5000,
          });
          setUploading(false);
          throw new Error("Banner with the same name already exists");
        } catch (error: any) {
          // If error is not about file not existing, re-throw it
          if (error.message === "Banner with the same name already exists") {
            throw error;
          }
          // File doesn't exist, proceed with upload
        }

        // Upload the banner
        const { url, uniqueId } = await uploadBanner(file);
        const imageName = file.name + uniqueId;
        onImageUrlChange(url);
        onImageNameChange(imageName);

        toast.current?.show({
          severity: "success",
          summary: "Upload Successful",
          detail: "Banner uploaded successfully",
          life: 3000,
        });

        return { url, imageName };
      } catch (error: any) {
        console.error("Upload error:", error);
        toast.current?.show({
          severity: "error",
          summary: "Upload Failed",
          detail: error.message || "An error occurred during upload",
          life: 5000,
        });
        throw error;
      } finally {
        setUploading(false);
      }
    };

    const processFile = async (file: File) => {
      if (!isBannerImageFile(file)) {
        showValidationError(
          "Please select an image file (JPG, PNG, GIF, or WebP)."
        );
        return;
      }

      const sizeError = getBannerFileSizeError(file);
      if (sizeError) {
        showValidationError(sizeError);
        return;
      }

      setValidating(true);
      try {
        const { width, height } = await loadImageDimensions(file);
        const dimensionError = getBannerDimensionError(width, height);
        if (dimensionError) {
          showValidationError(dimensionError);
          return;
        }

        setSelectedFile(file);
        onImageNameChange(file.name);

        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.onerror = () => {
          showValidationError(
            "Could not read this image. Try saving the file to your computer first."
          );
        };
        reader.readAsDataURL(file);

        toast.current?.show({
          severity: "success",
          summary: "Banner selected",
          detail: `${file.name} (${width} x ${height}) is ready to upload.`,
          life: 4000,
        });
      } catch {
        showValidationError(
          "Could not read this image. Try saving the file to your computer first."
        );
      } finally {
        setValidating(false);
      }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        toast.current?.show({
          severity: "warn",
          summary: "No file selected",
          detail:
            "The browser could not read that file. Try saving it to your computer first, then upload again.",
          life: 6000,
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      void processFile(file);
    };

    const validateAndUpload = async (): Promise<{
      url: string;
      imageName: string;
    }> => {
      if (!selectedFile) {
        throw new Error("Please choose a file first");
      }

      return performUpload(selectedFile);
    };

    // Expose upload function to parent via ref
    useImperativeHandle(ref, () => ({
      upload: validateAndUpload,
      hasFile: () => selectedFile !== null,
      clear: clearSelection,
    }));

    const handleDelete = () => {
      clearSelection();
      toast.current?.show({
        severity: "info",
        summary: "Cleared",
        detail: "Banner selection cleared",
        life: 2000,
      });
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files?.[0];
      if (!file) {
        toast.current?.show({
          severity: "warn",
          summary: "No file selected",
          detail: "Drop a single image file onto this area.",
          life: 4000,
        });
        return;
      }
      void processFile(file);
    };

    const busy = uploading || validating;

    return (
      <div className="banner-swap-form-upload">
        <Toast ref={toast} />
        <div className="flex flex-wrap gap-2">
          <Button
            label="Choose"
            icon="pi pi-file"
            onClick={handleChooseClick}
            disabled={busy}
            className="px-5 py-2.5 font-medium rounded-lg"
            style={{
              backgroundColor: "#4A90E2",
              color: "#fff",
              border: "none",
            }}
          />
          <Button
            label="Delete"
            icon="pi pi-trash"
            onClick={handleDelete}
            disabled={(!selectedFile && !previewUrl) || busy}
            className="px-5 py-2.5 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              border: "none",
            }}
          />
        </div>

        <div
          className="banner-swap-form-upload__dropzone border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 relative overflow-hidden cursor-pointer hover:border-gray-400 transition-colors"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleChooseClick}
        >
          <div className="p-5 w-full h-full flex items-center justify-center">
              {busy && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center z-10">
                  <div className="text-center text-white">
                    <i className="pi pi-spin pi-spinner text-4xl mb-2 block"></i>
                    <p className="m-0">
                      {uploading ? "Uploading..." : "Checking banner..."}
                    </p>
                  </div>
                </div>
              )}
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Banner preview"
                  className="rounded-md object-contain"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="text-center w-full text-gray-500">
                  <i className="pi pi-image text-5xl mb-3 block"></i>
                  <p className="m-0 font-medium">Drag and Drop File</p>
                  <p className="m-0 text-sm mt-1">or click to browse</p>
                </div>
              )}
            </div>
          </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }
);

FileUploadArea.displayName = "FileUploadArea";

export default FileUploadArea;
